import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { TrendingUp, Star, Users, Eye, QrCode, Download, Handshake, MessageSquare, ChevronRight, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CountUp, FadeUp, FunnelStep } from "@/components/Motion";

export default function SupplierSuccess({ presenterMode = false }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [self, setSelf] = useState({ social_shares: 0, lead_quality_avg: "", post_event_followups: 0, opportunity_pipeline_usd: 0, nps: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (presenterMode) {
      // Demo data so the slide visualises beautifully in presentations.
      setData({
        profile_views: 218, qr_scans: 64, lead_count: 41, meetings_requested: 18,
        meetings_completed: 11, post_event_followups: 7, capability_downloads: 9,
        social_shares: 12, lead_quality_avg: 4.2, opportunity_pipeline_usd: 184000, nps: 9,
        lead_score: 82, renewal_recommendation: "Highly Recommended",
        renewal_reason: "Strong engagement, healthy pipeline, and high satisfaction.",
        intros_requested_by_t_l: 4,
      });
      setSelf({ social_shares: 12, lead_quality_avg: 4.2, post_event_followups: 7, opportunity_pipeline_usd: 184000, nps: 9 });
      return;
    }
    if (!user) { nav("/login"); return; }
    // Try the extended endpoint first, fall back to legacy.
    api.get("/exhibitors/me/success-extended").then(({ data: ext }) => {
      api.get("/exhibitors/me/success").then(({ data: legacy }) => {
        const merged = { ...legacy, ...ext };
        setData(merged);
        setSelf({
          social_shares: merged.social_shares || 0,
          lead_quality_avg: merged.lead_quality_avg ?? "",
          post_event_followups: merged.post_event_followups || 0,
          opportunity_pipeline_usd: merged.opportunity_pipeline_usd || 0,
          nps: merged.nps ?? "",
        });
      });
    }).catch(() => { nav("/dashboard"); });
  }, [user, loading, nav, presenterMode]);

  const save = async () => {
    if (presenterMode) { toast.success("Saved (demo)"); return; }
    setSaving(true);
    try {
      const payload = {
        social_shares: Number(self.social_shares) || 0,
        post_event_followups: Number(self.post_event_followups) || 0,
        opportunity_pipeline_usd: Number(self.opportunity_pipeline_usd) || 0,
      };
      if (self.lead_quality_avg !== "") payload.lead_quality_avg = parseFloat(self.lead_quality_avg);
      if (self.nps !== "") payload.nps = parseInt(self.nps);
      await api.put("/exhibitors/me/success", payload);
      toast.success("Self-reported metrics saved.");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!data) return (<Layout><section className="container-summit py-24"><div className="overline">Loading…</div></section></Layout>);

  const headline = [
    { value: data.profile_views, label: "Profile Views", icon: Eye },
    { value: data.qr_scans, label: "QR Scans", icon: QrCode },
    { value: data.lead_count, label: "Leads Captured", icon: Users },
    { value: data.meetings_requested, label: "Meetings Requested", icon: Handshake },
  ];

  // Funnel: Views → QR Scans → Leads → Meetings → Completed → Followups
  const top = Math.max(1, data.profile_views, data.qr_scans, data.lead_count, data.meetings_requested);
  const funnel = [
    { label: "Profile Views", value: data.profile_views, color: "#A39171" },
    { label: "QR Scans", value: data.qr_scans, color: "#A39171" },
    { label: "Leads", value: data.lead_count, color: "#0A0A0A" },
    { label: "Meetings Requested", value: data.meetings_requested, color: "#0A0A0A" },
    { label: "Meetings Completed", value: data.meetings_completed, color: "#0A0A0A" },
    { label: "Post-event Follow-ups", value: data.post_event_followups || 0, color: "#0A0A0A" },
  ];

  const pipelineUSD = data.opportunity_pipeline_usd || 0;
  const roiX = pipelineUSD ? pipelineUSD / 2500 : 0;

  return (
    <Layout>
      {/* Hero */}
      <section className="border-b border-[hsl(var(--line))] bg-gradient-to-b from-white to-zinc-50">
        <div className="container-summit py-14 md:py-20">
          <FadeUp className="overline mb-6"><span className="accent-rule" />Supplier Success Dashboard</FadeUp>
          <FadeUp delay={0.05} className="flex items-end justify-between gap-6 flex-wrap">
            <h1 className="serif text-4xl sm:text-6xl">Your summit return.</h1>
            <Link to="/leads" className="btn-outline-black"><Users size={16} /> View Leads <ChevronRight size={14} /></Link>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-3 text-zinc-500 max-w-2xl">
            A consolidated view of the engagement your team is generating — and the pipeline you're building. Was the $2,500 investment worth it? The numbers below tell the story.
          </FadeUp>
        </div>
      </section>

      {/* Headline KPIs */}
      <section className="container-summit py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[hsl(var(--line))]">
          {headline.map((k, i) => (
            <FadeUp key={k.label} delay={i * 0.05}>
              <div className="bg-white p-6 md:p-8">
                <k.icon size={18} className="text-[#A39171]" strokeWidth={1.4} />
                <div className="serif text-5xl md:text-6xl tabular-nums leading-none mt-5">
                  <CountUp value={k.value || 0} />
                </div>
                <div className="overline mt-4">{k.label}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Pipeline Funnel + ROI + Lead Score + Renewal Recommendation */}
      <section className="container-summit pb-16 grid lg:grid-cols-[2fr_1fr] gap-6">
        <FadeUp>
          <div className="card-flat p-6 md:p-8 h-full">
            <h2 className="text-lg mb-2">Engagement Funnel</h2>
            <p className="text-sm text-zinc-500 mb-6">From first impression to follow-up — your full supplier conversion path.</p>
            <div className="grid gap-5">
              {funnel.map((f) => <FunnelStep key={f.label} {...f} total={top} />)}
            </div>
          </div>
        </FadeUp>

        <div className="grid gap-6">
          <FadeUp delay={0.05}>
            <div className="card-flat p-6 md:p-8 bg-[#0A0A0A] text-white">
              <div className="overline text-zinc-400">Estimated Opportunity Pipeline</div>
              <div className="serif text-5xl mt-4 tabular-nums">
                $<CountUp value={pipelineUSD} />
              </div>
              <div className="overline text-zinc-400 mt-3">Self-reported · USD</div>
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="overline text-zinc-400">Implied ROI</div>
                <div className="serif text-3xl mt-2 tabular-nums">
                  <CountUp value={Number(roiX.toFixed(1))} />×
                </div>
                <div className="overline text-zinc-400 mt-1">on the $2,500 package</div>
              </div>
            </div>
          </FadeUp>

          {(data.lead_score != null || data.renewal_recommendation) && (
            <FadeUp delay={0.1}>
              <div className="card-flat p-6 md:p-8">
                <div className="overline">Lead Score</div>
                <div className="flex items-end gap-4 mt-3">
                  <div className="serif text-6xl tabular-nums leading-none">
                    <CountUp value={data.lead_score || 0} />
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 pb-2">/ 100</div>
                </div>
                <div className="h-1 bg-zinc-100 mt-5">
                  <div className="h-full bg-[#A39171]" style={{ width: `${data.lead_score || 0}%`, transition: "width 0.8s ease" }} />
                </div>
                <div className="mt-8 pt-6 border-t border-zinc-100">
                  <div className="overline">2027 Renewal Recommendation</div>
                  <div className="serif text-2xl mt-3">{data.renewal_recommendation || "—"}</div>
                  {data.renewal_reason && (
                    <p className="text-sm text-zinc-600 mt-3 leading-relaxed">{data.renewal_reason}</p>
                  )}
                </div>
              </div>
            </FadeUp>
          )}
        </div>
      </section>

      {/* Secondary metrics */}
      <section className="container-summit pb-16">
        <FadeUp className="overline mb-5"><span className="accent-rule" />Quality & Reach</FadeUp>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[hsl(var(--line))]">
          {[
            { label: "Capability Downloads", value: data.capability_downloads || 0 },
            { label: "Social Shares", value: data.social_shares || 0 },
            { label: "Lead Quality (avg)", value: data.lead_quality_avg ?? "—" },
            { label: "NPS", value: data.nps ?? "—" },
          ].map((t) => (
            <div key={t.label} className="bg-white p-6">
              <div className="serif text-3xl">{typeof t.value === "number" ? <CountUp value={t.value} /> : t.value}</div>
              <div className="overline mt-2">{t.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Self-report form */}
      <section className="container-summit pb-16">
        <FadeUp>
          <div className="card-flat p-6 md:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
              <div>
                <h2 className="text-xl">Self-Reported Metrics</h2>
                <p className="text-sm text-zinc-500 mt-1">Update these after the event to power the renewal conversation.</p>
              </div>
              <button onClick={save} disabled={saving} className="btn-black" data-testid="success-save">
                <Save size={16} /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              <SF k="social_shares" label="Social Shares" v={self.social_shares} on={setSelf} type="number" />
              <SF k="lead_quality_avg" label="Lead Quality Avg (1–5)" v={self.lead_quality_avg} on={setSelf} type="number" step="0.1" />
              <SF k="post_event_followups" label="Post-event Follow-ups" v={self.post_event_followups} on={setSelf} type="number" />
              <SF k="opportunity_pipeline_usd" label="Opportunity Pipeline ($)" v={self.opportunity_pipeline_usd} on={setSelf} type="number" />
              <SF k="nps" label="NPS (0–10)" v={self.nps} on={setSelf} type="number" />
            </div>
          </div>
        </FadeUp>
      </section>
    </Layout>
  );
}

function SF({ k, label, v, on, type = "text", step }) {
  return (
    <div>
      <Label className="overline">{label}</Label>
      <Input type={type} step={step} value={v} onChange={(e) => on((s) => ({ ...s, [k]: e.target.value }))}
        className="h-11 rounded-none mt-2" data-testid={`success-${k}`} />
    </div>
  );
}
